import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import {
  getAccessContextFromAuth,
  getDenyMessage,
  isAccessPending,
  isSystemAccessRule,
  normalizePathname,
  resolveRouteAccess,
} from '../config/routeAccess';
import type { AccessDecision } from '../config/routeAccessTypes';
import { ALWAYS_ALLOWED_PATHS } from '../utils/permisoKeys';

export interface RouteAccessState {
  decision: AccessDecision;
  denyMessage: string;
  pathname: string;
}

export function useRouteAccess(): RouteAccessState {
  const location = useLocation();
  const params = useParams();
  const { user, isAuthenticated } = useAuth();
  const { canPage, ready } = usePermissions();

  return useMemo(() => {
    const pathname = location.pathname;
    const pendingRoles = isAccessPending(isAuthenticated, user?.roles);
    if (pendingRoles) {
      return { decision: 'pending' as const, denyMessage: '', pathname };
    }

    // Menú principal y rutas libres: no esperar catálogo (evita parpadeo al aterrizar).
    if (ALWAYS_ALLOWED_PATHS.has(normalizePathname(pathname))) {
      return { decision: 'allow', denyMessage: '', pathname };
    }

    const pendingPermisos = isAuthenticated && !ready;

    const ctx = getAccessContextFromAuth(pathname, params, user);
    const resolved = resolveRouteAccess(ctx);
    if (resolved.decision === 'deny') {
      return {
        decision: 'deny',
        denyMessage: getDenyMessage(resolved.denyMessageKey),
        pathname,
      };
    }

    // Administración / sistema: por rol, no por catálogo de permisos.
    if (isSystemAccessRule(resolved.ruleId)) {
      return { decision: 'allow', denyMessage: '', pathname };
    }

    if (pendingPermisos) {
      return { decision: 'pending' as const, denyMessage: '', pathname };
    }

    if (!canPage(pathname)) {
      return {
        decision: 'deny',
        denyMessage: getDenyMessage('page.required'),
        pathname,
      };
    }

    return {
      decision: 'allow',
      denyMessage: '',
      pathname,
    };
  }, [canPage, isAuthenticated, location.pathname, params, ready, user]);
}
